import styles from './edit-company-details-dialog.module.scss';
import { useState } from 'react';
import { Col, Row } from 'reactstrap';
import { Autocomplete, Checkbox, CheckboxLabel, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, ErrorButton, FormControl, GrayOutlinedButton, Icon, IUserAccount, ListItemText, MenuItem, PrimaryButton, SecondaryButton, Select, TextField } from '@frontend-workspace/react-shared-lib';
import { ICompanyUserAccounts } from '../_models/company-user-accounts';
import { ICompanyDetails } from '../_models/company-details';
import { useTranslation } from 'react-i18next';
import { useMakeRequestHook } from '../requestUtils';

/* eslint-disable-next-line */
export interface EditCompanyDetailsDialogProps {
  company: ICompanyDetails,
  onClose: () => void,
  coAdmins: IUserAccount[],
  userAccounts: ICompanyUserAccounts[],
  admin: IUserAccount
}

export function EditCompanyDetailsDialog(props: EditCompanyDetailsDialogProps) {
  const { t } = useTranslation();
  const makeRequest = useMakeRequestHook();
  const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState<boolean>(props.company.twoFactorAuthentication);
  const [automaticallyApprovedDomains, setAutomaticallyApprovedDomains] = useState<string[]>(props.company.automaticallyApprovedDomains);
  const [newAutomaticallyApprovedDomain, setNewAutomaticallyApprovedDomain] = useState<string>("");
  const [selectedCoAdminIds, setSelectedCoAdminIds] = useState(props.coAdmins.map(ua => ua.id));
  const [companyLanguage, setCompanyLanguage] = useState<string>(props.company.language);
  const [adminId, setAdminId] = useState(props.admin?.id);
  const [federatedDomain, setFederatedDomain] = useState<string>(props.company.federatedDomain || '');
  const [federationTenantId, setFederationTenantId] = useState<string>(props.company.federationTenantId || '');
  const [federationClientId, setFederationClientId] = useState<string>(props.company.federationClientId || '');

  const handleCoAdminChange = (accountId: string, checked: boolean) => {
    if (checked) setSelectedCoAdminIds([...selectedCoAdminIds, accountId]);
    else setSelectedCoAdminIds(selectedCoAdminIds.filter(id => id !== accountId));
  };

  const handleRequest = () => {
    makeRequest(`companies/${props.company.companyNumber}/action/changeCompanyDetails`, 'PUT', {
      isTwoFactorRequired: isTwoFactorEnabled,
      language: companyLanguage,
      adminId: adminId,
      coAdminIds: selectedCoAdminIds,
      automaticallyApprovedDomains: automaticallyApprovedDomains,
      federatedDomain: federatedDomain,
      federationTenantId: federationTenantId,
      federationClientId: federationClientId
    }).then(result => {
      props.onClose();
    });
  };

  return (
    <Dialog
      open={true}
      onClose={props.onClose}
      fullWidth
      maxWidth="md">
      <DialogTitle className='mb-4'>
        {t("editCompanyDetails.changeCompanyDetails")}
      </DialogTitle>
      <DialogContent>
        <div className='d-flex flex-column'>
          <div className='mb-3'>
            <Select
              label={t("standard.selectAdministrator")}
              name='adminId'
              fullWidth
              value={adminId || ''}
              onChange={(event: any) => setAdminId(event.target.value)}>
              {props.userAccounts.map(ua => {
                return <MenuItem key={ua.id} value={ua.id}>
                  {ua.email}
                </MenuItem>
              })}
            </Select>
          </div>
          <div className='mb-3'>
            <FormControl fullWidth>
              <Autocomplete
                disablePortal={true}
                value={selectedCoAdminIds || []}
                multiple
                limitTags={1}
                fullWidth
                options={props.userAccounts}
                getOptionLabel={(groupId: any) => {
                  const account = props.userAccounts.find(ua => ua.id === groupId);
                  return account?.email || t("editCompanyDetails.noEmail");
                }}
                renderInput={(params: any) => {
                  return <TextField
                    {...params}
                    fullWidth
                    label={t("editCompanyDetails.selectCoAdministrators")}
                  />
                }}
                filterOptions={(options, state: any) => {
                  return options.filter(option =>
                    option.email.toLowerCase().includes(state.inputValue.toLowerCase())
                  );
                }}
                renderOption={(props: any, userAccount: ICompanyUserAccounts) => {
                  return <MenuItem key={userAccount.id} value={userAccount.id} >
                    <Checkbox
                      checked={selectedCoAdminIds.includes(userAccount.id)}
                      onChange={(event: any, checked: boolean) => handleCoAdminChange(userAccount.id, checked)}
                    />
                    <ListItemText primary={userAccount.email} />
                  </MenuItem>
                }}
                onChange={(event: any, groupIds: any) => setSelectedCoAdminIds(groupIds)}
              />
            </FormControl>
          </div>
        </div>

        <Row>
          <Col>
            <Select
              label={t("standard.language")}
              name='companyLanguage'
              value={companyLanguage}
              onChange={(event: any) => setCompanyLanguage(event.target.value)}
              fullWidth
              className='mb-3'>
              <MenuItem value={"NOR"}>
                {t("editCompanyDetails.norwegian")}
              </MenuItem>
              <MenuItem value={"ENG"}>
                {t("editCompanyDetails.english")}
              </MenuItem>
            </Select>
          </Col>

          <Col className='d-flex align-items-end mb-3'>
            <CheckboxLabel
              checked={isTwoFactorEnabled || false}
              label={t("editCompanyDetails.twoFactorAuthentication")}
              onChange={() => setIsTwoFactorEnabled(!isTwoFactorEnabled)}
            />
          </Col>
        </Row>
        <Divider className="my-4"><Chip label={t("standard.domainsThatAutomaticallyApproveAccounts")} /></Divider>
        <Row>
          <Col>
            {automaticallyApprovedDomains.map((domain, index) => {
              return (
                <div key={index} className='d-flex align-items-end mb-3'>
                  <TextField
                    fullWidth
                    value={domain || ''}
                    label={t("standard.domain")}
                    disabled
                    className='me-3'
                  />
                  <ErrorButton
                    variant='outlined'
                    color='secondary'
                    className='minButtonWidth ms-3'
                    onClick={() => setAutomaticallyApprovedDomains(automaticallyApprovedDomains.filter((_, i) => i !== index))}>
                    <Icon fontSize="small" className="me-1">delete</Icon>{t("standard.delete")}
                  </ErrorButton>
                </div>
              );
            })}
            <div className='d-flex align-items-end mb-3'>
              <TextField
                fullWidth
                label={t("standard.domain")}
                value={newAutomaticallyApprovedDomain || ''}
                onChange={(event: any) => setNewAutomaticallyApprovedDomain(event.target.value)}
              />
              <SecondaryButton
                variant='contained'
                onClick={() => {
                  setAutomaticallyApprovedDomains([...automaticallyApprovedDomains, newAutomaticallyApprovedDomain]);
                  setNewAutomaticallyApprovedDomain('');
                }}
                className='minButtonWidth ms-3'>
                <Icon fontSize="small" className="me-1">add</Icon>
                {t("standard.add")}
              </SecondaryButton>
            </div>
          </Col>
        </Row>

        <Divider className="my-4"><Chip label={t("editCompanyDetails.federationAndSSO")} /></Divider>
        <Row>
          <Col>
            <div className='d-flex align-items-end mb-3'>
              <TextField
                fullWidth
                label={t("editCompanyDetails.domain")}
                value={federatedDomain}
                onChange={(event: any) => setFederatedDomain(event.target.value)}
              />
            </div>            
          </Col>
        </Row>

        <Row>
          <Col>
            <div className='d-flex align-items-end mb-3'>
              <TextField
                fullWidth
                label={t("editCompanyDetails.tenantId")}
                value={federationTenantId}
                onChange={(event: any) => setFederationTenantId(event.target.value)}
              />
            </div>            
          </Col>
        </Row>

        <Row>
          <Col>
            <div className='d-flex align-items-end mb-3'>
              <TextField
                fullWidth
                label={t("editCompanyDetails.clientId")}
                value={federationClientId}
                onChange={(event: any) => setFederationClientId(event.target.value)}
              />
            </div>            
          </Col>
        </Row>


      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={handleRequest}
          className='minButtonWidth'>
          {t("standard.save")}
        </PrimaryButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog >
  );
}

export default EditCompanyDetailsDialog;